import {Helmet} from 'react-helmet-async';
import {useLocation} from 'react-router-dom';

import {SITE_CONFIG} from '../site.config';

import {SEOProps} from '../declarations';

const SEO: React.FC<SEOProps> = ({
    title,
    description,
    image = '/og-image.png',
    keywords = SITE_CONFIG.seo.keywords,
    noIndex = false,
    canonical,
}) => {
    const {pathname} = useLocation();
    const currentYear = new Date().getFullYear();

    /* -------------------- Page Derived Values -------------------- */
    const pageTitle = title
        ? `${title} | ${SITE_CONFIG.name}`
        : SITE_CONFIG.seo.defaultTitle;
    const pageDescription = description || SITE_CONFIG.seo.defaultDescription;
    const pageUrl = canonical || `${SITE_CONFIG.url}${pathname}`;
    const pageImage = image.startsWith('http')
        ? image
        : `${SITE_CONFIG.url}${image}`;
    const robots = noIndex ? 'noindex, nofollow' : 'index, follow';
    const ogLocale = 'en_US';

    /* -------------------- Structured Data (JSON-LD) -------------------- */
    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: pageTitle,
        description: pageDescription,
        url: pageUrl,
        inLanguage: 'en',
        isPartOf: {
            '@type': 'WebSite',
            name: SITE_CONFIG.name,
            url: SITE_CONFIG.url,
            description: SITE_CONFIG.description,
        },
        publisher: {
            '@type': 'Organization',
            name: SITE_CONFIG.legal.company,
            url: SITE_CONFIG.url,
            logo: `${SITE_CONFIG.url}/logo.png`,
            sameAs: Object.values(SITE_CONFIG.social),
        },
    };

    /* -------------------- Meta Tags Configuration -------------------- */
    const metaKeywords = keywords.join(',');
    const openGraph = {
        title: pageTitle,
        description: pageDescription,
        url: pageUrl,
        image: pageImage,
    };
    const twitter = {
        title: pageTitle,
        description: pageDescription,
        image: pageImage,
    };
    const mobileConfig = {
        viewport: 'width=device-width, initial-scale=1, maximum-scale=5',
        themeColor: '#1a1a2e',
    };

    /* -------------------- Render Head -------------------- */
    return (
        <Helmet>
            {/* Primary Meta */}
            <title>{pageTitle}</title>
            <meta name='description' content={pageDescription} />
            <meta name='keywords' content={metaKeywords} />
            <meta name='language' content='en' />
            <meta name='robots' content={robots} />
            <meta name='author' content={SITE_CONFIG.legal.company} />
            <meta
                name='copyright'
                content={`Copyright © ${currentYear} ${SITE_CONFIG.legal.company}`}
            />

            {/* Open Graph */}
            <meta property='og:title' content={openGraph.title} />
            <meta property='og:description' content={openGraph.description} />
            <meta property='og:url' content={openGraph.url} />
            <meta property='og:image' content={openGraph.image} />
            <meta property='og:type' content='website' />
            <meta property='og:site_name' content={SITE_CONFIG.name} />
            <meta property='og:locale' content={ogLocale} />

            {/* Twitter Cards */}
            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={twitter.title} />
            <meta name='twitter:description' content={twitter.description} />
            <meta name='twitter:image' content={twitter.image} />

            {/* Canonical */}
            <link rel='canonical' href={pageUrl} />

            {/* Structured Data */}
            <script type='application/ld+json'>
                {JSON.stringify(structuredData)}
            </script>

            {/* PWA / Mobile Configuration */}
            <meta name='viewport' content={mobileConfig.viewport} />
            <meta name='theme-color' content={mobileConfig.themeColor} />
            <meta name='mobile-web-app-capable' content='yes' />
            <meta name='apple-mobile-web-app-capable' content='yes' />
            <meta
                name='apple-mobile-web-app-status-bar-style'
                content='black-translucent'
            />
        </Helmet>
    );
};

export default SEO;
